import _ from "underscore"
import BasePDFDefinitionCreator from "../BasePDFDefinitionCreator"
import LeavesReportUtils from "../../reports/leavesReport/LeavesReportUtils"
import {atImgPath} from "../../atBrowserUtils"
import messageResource from "../../messageResource"
import BalanceType from "../../enums/BalanceType"
import {Feature, isEnabled} from "../../features/index"
import PDFImageCache from "../PDFImageCache"
import {PaperOrientation, PaperSize} from "../constants"
import PDFDefinitionBuilder from "./PDFDefinitionBuilder"
import DataTableCreateDefinitionHelper from "./DataTableCreateDefinitionHelper"
import * as PDFDefinitionColumnBuilder from "./PDFDefinitionColumnBuilder"
import {REPORT_LOGO_PATH} from "../constants";
import LeavesReportFormatUtils from "../../reports/leavesReport/FormatUtils";
import {parameters as leavesReportPdfParameters} from "./PDFDefinitionBuilder"

export default class PDFDefinitionCreator extends BasePDFDefinitionCreator {

    createDocDefinition(pdfConfig, parameterFormatter, store) {
        this.parameterFormatter = parameterFormatter;
        this.pdfPrintParams = pdfConfig.pdfPrintParameters;
        this.pdfData = pdfConfig.pdfData;
        this.pdfConfig = pdfConfig;
        this.reportParams = pdfConfig.reportParams;
        this.dateFormat = this.pdfConfig.dateFormat;

        var hasLeaves = LeavesReportUtils.reportParamsWithLeaves(this.reportParams);
        var hasPto = LeavesReportUtils.reportParamsWithPTO(this.reportParams);
        var hasSick = LeavesReportUtils.reportParamsWithSick(this.reportParams);
        var leavesNumber = this.pdfData.leaveTypeIds.length;

        var columns = [];
        columns.push(new PDFDefinitionColumnBuilder.Levels().init(this.reportParams, leavesNumber, hasLeaves, hasPto, hasSick, store,
            this.pdfPrintParams.paperOrientation));

        if (hasLeaves) {
            columns.push(new PDFDefinitionColumnBuilder.Totals().init(this.reportParams));
            for (var i = 0; i < leavesNumber; i++) {
                var leaveTypeId = this.pdfData.leaveTypeIds[i];
                columns.push(new PDFDefinitionColumnBuilder.Leave().init(this.reportParams, leaveTypeId, i));
            }
        }

        if (hasPto) {
            columns.push(new PDFDefinitionColumnBuilder.Balance().init(this.reportParams, BalanceType.PTO,
                this.pdfData.defaultWorkDayDuration));
        }

        if (hasSick) {
            columns.push(new PDFDefinitionColumnBuilder.Balance().init(this.reportParams, BalanceType.SICK,
                this.pdfData.defaultWorkDayDuration));
        }

        var ddb = new PDFDefinitionBuilder();
        ddb.init(this.pdfPrintParams.paperOrientation, this.pdfPrintParams.paperSize, columns);

        // start create doc definition
        return PDFImageCache
            .preloadImages(this._imagesMap(pdfConfig)) // preload needed images
            .then(
                _.bind(this._processCreateDocDefinition, this, ddb) // construct doc definition
            )
            .then(function() {
                // return object for generate pdf
                return {
                    "docDefinition": ddb.getDocDefinitionObject(),
                    "tableLayouts": ddb.getTableLayouts()
                }
            });
    }

    _imagesMap() {
        var imagesMap = {
            "logo": {
                "url": REPORT_LOGO_PATH
            },
            "filterIcon": {
                "url": atImgPath('filter-for-pdf.png'),
                "saveAlpha": true
            },
            "noteIcon": {
                "url": atImgPath('support/note_mark.gif'),
                "saveAlpha": true
            }
        };

        _.each(LeaveTypeStore.getLeaveTypes(), function(leaveType) {
            var iconId = leaveType["iconId"];

            imagesMap["leaveTypeIcon_" + iconId] = {
                "url": getLeaveTypeIconPath(iconId),
                "saveAlpha": true
            };
        });

        return imagesMap;
    }

    _processCreateDocDefinition(ddb) {
        ddb.initDocDefinitionObject(this.pdfPrintParams.showSignatureOnEachPage && this.pdfPrintParams.showSignature, this.pdfPrintParams.showHeaderOnEachPage);

        var headerData = this._createHeaderData();
        if (this.pdfPrintParams.paperOrientation === PaperOrientation.PORTRAIT) {
            ddb.addPortraitHeader(this.pdfPrintParams.showHeaderOnEachPage, this.pdfPrintParams.showLogoInHeader, headerData);
        }
        else {
            ddb.addLandscapeHeader(this.pdfPrintParams.showHeaderOnEachPage, this.pdfPrintParams.showLogoInHeader, headerData)
        }

        if (this.pdfConfig.chartSectionConfig.showChart && this.pdfData.chartImage != null) {
            var config = this.pdfConfig.chartSectionConfig;

            var grouping = LeavesReportFormatUtils.formatGroupingLevel(this.reportParams.firstGroupingLevel);
            var chartType = LeavesReportFormatUtils.formatChartType(config["chartType"]);

            var viewBy = messageResource.getMessage("common.pdf.chart.view_by_label", [chartType, grouping]);
            ddb.addChartImage(viewBy, this.pdfData.chartImage);

            var fromDateFormatted = this.dateFormat.formatDate(this.reportParams["dateRangeSelection"]["fromDate"]);
            var toDateFormatted = this.dateFormat.formatDate(this.reportParams["dateRangeSelection"]["toDate"]);
            var totals = this.pdfData.totals;

            var title;
            var total = "";
            var items = [];
            var unitsKey;
            var units;
            if (config["chartType"] == APReportChartType.LEAVE_TIME) {
                title = messageResource.getMessage("common.leave.reports.pdf.total_leave_time");
                total = LeavesReportFormatUtils.formatTime(totals.totalValue, this.reportParams.timeFormat);

                _.each(this.pdfData.leaveTypeIds, function(leaveId) {
                    var leave = LeaveTypeStore.getLeaveType(leaveId);
                    items.push({
                        name: leave.name,
                        color: colorMap['leaveType' + leave['iconId']],
                        originalValue: totals.totalsMap[leaveId],
                        value: LeavesReportFormatUtils.formatTime(totals.totalsMap[leaveId], this.reportParams.timeFormat)
                    });
                }, this);
            }
            else if (config["chartType"] == APReportChartType.PTO_BALANCE) {
                unitsKey = window.SystemSettings.showPTOInHours ? "common.pto_units.hours" : "common.pto_units.days";
                units = messageResource.getMessage(unitsKey);
                title = messageResource.getMessage("common.pdf.chart.total_pto", [units]);

                items.push({
                    name: messageResource.getMessage("common.pdf.chart.total_pto_as_of", [fromDateFormatted]),
                    color: colorMap['ptoBefore'],
                    originalValue: totals.ptoBalance['balanceForFirstColumn'],
                    value: LeavesReportFormatUtils.formatBalance(totals.ptoBalance['balanceForFirstColumn'], BalanceType.PTO,
                        this.pdfData.defaultWorkDayDuration)
                });
                items.push({
                    name: messageResource.getMessage("common.pdf.chart.total_pto_as_of", [toDateFormatted]),
                    color: colorMap['ptoAfter'],
                    originalValue: totals.ptoBalance['balanceForSecondColumn'],
                    value: LeavesReportFormatUtils.formatBalance(totals.ptoBalance['balanceForSecondColumn'], BalanceType.PTO,
                        this.pdfData.defaultWorkDayDuration)
                });
            }
            else {
                unitsKey = window.SystemSettings.showSickInHours ? "common.pto_units.hours" : "common.pto_units.days";
                units = messageResource.getMessage(unitsKey);
                title = messageResource.getMessage("common.pdf.chart.total_sick", [units]);

                items.push({
                    name: messageResource.getMessage("common.pdf.chart.total_sick_as_of", [fromDateFormatted]),
                    color: colorMap['sickBefore'],
                    originalValue: totals.sickBalance['balanceForFirstColumn'],
                    value: LeavesReportFormatUtils.formatBalance(totals.sickBalance['balanceForFirstColumn'], BalanceType.SICK,
                        this.pdfData.defaultWorkDayDuration)
                });
                items.push({
                    name: messageResource.getMessage("common.pdf.chart.total_sick_as_of", [toDateFormatted]),
                    color: colorMap['sickAfter'],
                    originalValue: totals.sickBalance['balanceForSecondColumn'],
                    value: LeavesReportFormatUtils.formatBalance(totals.sickBalance['balanceForSecondColumn'], BalanceType.SICK,
                        this.pdfData.defaultWorkDayDuration)
                });
            }

            ddb.addChartLegend(title, items, total);
        }

        this._addTableData(ddb);

        if ((isEnabled(Feature.PTO) || isEnabled(Feature.SDB))
            && (LeavesReportUtils.reportParamsWithPTO(this.reportParams) || LeavesReportUtils.reportParamsWithSick(this.reportParams)))
            this._addNotes(ddb);
    }

    getChartImageSizes(pdfPrintParameters) {
        var pdfParameters = leavesReportPdfParameters;
        if (pdfPrintParameters.paperOrientation === PaperOrientation.LANDSCAPE) {
            pdfParameters = pdfParameters.Landscape;
        }
        else {
            pdfParameters = pdfParameters.Portrait;
        }

        if (pdfPrintParameters.paperSize === PaperSize.A4) {
            pdfParameters = pdfParameters.A4;
        }
        else {
            pdfParameters = pdfParameters.Letter;
        }

        return {
            width: pdfParameters.chartImageSize.width * 2,
            height: pdfParameters.chartImageSize.height * 2
        }
    }

    _createHeaderData() {
        var selectedParameters = this.pdfConfig.reportParams;
        var assignedInfo = this.pdfConfig.assignedInfo;

        return {
            informationTexts: {
                title: this.parameterFormatter.formatChartPDFReportTitle(selectedParameters),
                dateRange: this.parameterFormatter.formatChartPDFHeaderDateInformation(selectedParameters)
            },
            filterTexts: this.parameterFormatter.formatChartPDFFilterInformation(selectedParameters, assignedInfo),
            logo: PDFImageCache.getImage('logo')
        }
    }

    _addTableData(ddb) {
        new DataTableCreateDefinitionHelper(ddb, this.pdfData.tableData, {
            dateFormat: this.dateFormat
        }).createTable();
    }

    _addNotes(ddb) {
        const note = !isEnabled(Feature.SDB) ? "report.leaves.pto_shown_within_date_range" :
                   !isEnabled(Feature.PTO) ? "report.leaves.sick_shown_within_date_range" :
                   "report.leaves.balances_shown_within_date_range";

        ddb.addIconedNote(messageResource.getMessage(note));
    }
}

const colorMap = {
    ptoBefore: '#6898D3',
    ptoAfter: '#86C087',
    sickBefore: '#D97B79',
    sickAfter: '#E3B463',

    leaveType1: '#F67C62',
    leaveType2: '#66A5F3',
    leaveType3: '#BEE470',
    leaveType4: '#FFD953',
    leaveType5: '#A68CDB',
    leaveType6: '#B6AE6C',
    leaveType7: '#C49B6F',
    leaveType8: '#E36AC9',
    leaveType9: '#646CC1',
    leaveType10: '#D88F64',
    leaveType11: '#ACC8E5',
    leaveType12: '#6ACE74',
    leaveType13: '#DC3030',
    leaveType14: '#8238E5',
    leaveType15: '#F1B61E',
    leaveType16: '#8B98A0',
    leaveType17: '#91DF75',
    leaveType18: '#F95660',
    leaveType19: '#A461FF',
    leaveType20: '#E2E53E',
    leaveType21: '#C47DFB',
    leaveType22: '#7DCCF9',
    leaveType23: '#FFA451',
    leaveType24: '#82E1DC'
}



// WEBPACK FOOTER //
// ./web/js/pdf/leavesReport/PDFDefinitionCreator.js